import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import {
  Banner,
  PageHeader,
  RegistryLayout,
  RegistryTable,
  WS_COLOR,
  WorkspaceThemeProvider,
  type RegistryColumn,
} from '@platform/ui-kit/workspace';
import { api, type AirPlatform } from '../../../api';

/**
 * Strategic Intelligence → AI Platforms Catalog, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `AiPlatformsCatalog.tsx`, which stays
 * the default until the owner approves this one (CLAUDE.md 1.2). It uses layout 2, Registry.
 *
 * WHAT IS UNCHANGED: the one API call, the fields on each platform (generated badge, name,
 * category, positioning cut at 110 characters, the "Cost:" label from the first assessment area
 * beginning "Cost"), the badge colours (the source's hash), the subtitle copy, and opening a
 * platform at `.../ai-platforms-catalog/<id>`.
 *
 * WHAT CHANGES, by the design system and nothing else: the cards become registry rows, which is
 * the pattern for a list of records (the PATTERN is the thing to approve). The page header is
 * locked and the table is the one scroll region. Rows open with Enter and Space as well as click.
 */

/** The source's `platBadge()`, as in the original: same initials, same hash, same hues. */
function platformBadge(p: AirPlatform): { initials: string; c1: string; c2: string } {
  const base = String(p.platform_code || p.name || '?')
    .replace(/\(.*/, '')
    .trim();
  const initials =
    base.length <= 4
      ? base.toUpperCase()
      : base
          .split(/[\s/+-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0])
          .join('')
          .toUpperCase();
  const s = String(p.name || base);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return {
    initials: initials || '?',
    c1: `hsl(${h},52%,42%)`,
    c2: `hsl(${(h + 28) % 360},58%,52%)`,
  };
}

/** The first assessment area beginning "Cost", as the original reads it. */
function costLabel(p: AirPlatform): string | null {
  const assessment = p.profile?.assessment;
  if (!Array.isArray(assessment)) return null;
  const c = assessment.find((a) => typeof a?.area === 'string' && a.area.indexOf('Cost') === 0);
  return c?.label ?? null;
}

const COLUMNS: RegistryColumn<AirPlatform>[] = [
  {
    key: 'name',
    header: 'Platform',
    width: 280,
    cell: (p) => {
      const badge = platformBadge(p);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              width: 28,
              height: 28,
              borderRadius: '7px',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${badge.c1}, ${badge.c2})`,
            }}
          >
            {badge.initials}
          </Box>
          <Box component="span" sx={{ fontWeight: 600, color: WS_COLOR.primary }}>
            {p.name}
          </Box>
        </Box>
      );
    },
  },
  { key: 'category', header: 'Category', width: 200, cell: (p) => p.category || '' },
  {
    key: 'positioning',
    header: 'Positioning',
    // `profile.positioning` ONLY, as the original does: the catalogue query does not select
    // the `positioning` column, so a fallback to it would be dead code.
    cell: (p) => (p.profile?.positioning ?? '').slice(0, 110),
  },
  {
    key: 'cost',
    header: 'Cost',
    width: 160,
    cell: (p) => costLabel(p) ?? '',
  },
];

export function AiPlatformsCatalogWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

function Screen() {
  const navigate = useNavigate();
  // Stay in the workspace version: the switch lives in the query string.
  const { search } = useLocation();
  const [platforms, setPlatforms] = useState<AirPlatform[]>([]);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.aiReadiness
      .getPlatforms()
      .then((d) => {
        if (active) setPlatforms(d.platforms);
      })
      .catch((e) => {
        if (active) setError(e?.message || 'Could not load the catalog.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const subtitle =
    `${platforms.length} AI platforms in the catalog. Each profile is generic — what the platform is, ` +
    `how it runs, and a practical at-a-glance read. Scoring against opportunities happens later, in the Fit tool.`;

  return (
    <RegistryLayout
      header={<PageHeader title="AI Platforms" subtitle={loading ? undefined : subtitle} />}
      filters={error ? <Banner tone="error">{error}</Banner> : undefined}
    >
      <RegistryTable
        label="AI platforms"
        rows={platforms}
        columns={COLUMNS}
        getRowId={(p) => String(p.id)}
        state={loading ? { kind: 'loading' } : { kind: 'ready' }}
        onSelect={(p) =>
          navigate(`/ai_readiness/strategic-intelligence/ai-platforms-catalog/${p.id}${search}`)
        }
      />
    </RegistryLayout>
  );
}
